import React from 'react';
import { LayoutDashboard, Package, Tags, History, Settings, BarChart3, LogOut, List, ShoppingCart, Truck, FileText, Package2, Building2, MapPin, ArrowRightLeft, FlaskRound as Flask, Trash2, Wrench, AlertCircle, ShieldCheck, Users } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS, PermissionName } from '../../lib/permissions';

interface NavigationItem {
  name: string;
  href: string;
  icon: any;
  requiresPermission?: PermissionName;
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, hasPermission } = useAuth();
  const currentPath = location.pathname;

  const navigation: NavigationItem[] = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Mismatch Inventory', href: '/mismatch-inventory', icon: AlertCircle },
    { name: 'Supplies', href: '/supplies', icon: Package },
    { name: 'Products', href: '/products', icon: Package2 },
    { name: 'R & D Inventory', href: '/rd-inventory', icon: Flask },
    { name: 'R&D Waste Management', href: '/rd-waste', icon: Trash2 },
    { name: 'Tools', href: '/tools', icon: Wrench },
    { name: 'Categories', href: '/categories', icon: Tags },
    { name: 'Sales Orders', href: '/sales-orders', icon: ShoppingCart },
    { name: 'Purchase Orders', href: '/purchase-orders', icon: FileText },
    { name: 'Receiving', href: '/receiving', icon: Package2 },
    { name: 'Shipping', href: '/shipping', icon: Truck },
    { name: 'Warranties', href: '/warranties', icon: ShieldCheck },
    { name: 'Suppliers', href: '/suppliers', icon: Building2 },
    { name: 'Locations', href: '/locations', icon: MapPin },
    { name: 'Transfers', href: '/location-transfers', icon: ArrowRightLeft },
    { name: 'Transactions', href: '/transactions', icon: History },
    { name: 'Reports', href: '/reports', icon: BarChart3 },
    { name: 'Audit Trail', href: '/audit-trail', icon: List },
    {
      name: 'User Management',
      href: '/user-management',
      icon: Users,
      requiresPermission: PERMISSIONS.USERS_VIEW
    },
    { name: 'Settings', href: '/settings', icon: Settings },
  ];

  const isActive = (path: string) => {
    if (path === '/' && currentPath === '/') return true;
    if (path !== '/' && currentPath.startsWith(path)) return true;
    return false;
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center flex-shrink-0 px-4 border-b border-gray-200">
        <div className="flex items-center">
          <Package className="h-8 w-8 text-blue-900" />
          <span className="ml-2 text-xl font-bold text-gray-900">InventoryHub</span>
        </div>
      </div>
      <nav className="flex-1 px-2 py-4 bg-white space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          if (item.requiresPermission && !hasPermission(item.requiresPermission)) {
            return null;
          }

          const IconComponent = item.icon;
          const active = isActive(item.href);
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`${
                active
                  ? 'bg-blue-50 text-blue-900 font-semibold'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150`}
            >
              <IconComponent
                className={`${
                  active ? 'text-blue-900' : 'text-gray-500 group-hover:text-gray-600'
                } mr-3 flex-shrink-0 h-5 w-5 transition-colors duration-150`}
              />
              {item.name}
            </Link>
          );
        })}
      </nav>
      <div className="flex-shrink-0 border-t border-gray-200 p-4">
        <div className="flex-shrink-0 w-full group block">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="h-8 w-8 rounded-full bg-blue-900 flex items-center justify-center text-white">
                {user?.email?.[0].toUpperCase()}
              </div>
              <div className="ml-3">
                <p className="text-sm font-semibold text-gray-800 group-hover:text-gray-900">
                  {user?.email}
                </p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="p-1 text-gray-600 hover:text-gray-700"
              title="Sign out"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;